// VersoBot, on the screen the operator is already looking at.
//
// Kevin, 2026-09-09: "let's add the versobot to the UI." Same assistant as in Slack — same tools,
// same rules, same prompt (lib/slackBot.js exports it, surface-aware, rather than a second copy
// that would drift). What differs here is that it is READ-ONLY: no create_ticket, because the
// support form is one tab away and a chat box is the most injection-exposed input we have.
//
// WHY IT LIVES IN THE SUPPORT MODAL. The question an operator asks a bot and the question they
// raise a ticket about are the same question, usually in that order — "why isn't this registered"
// and then "please look at this". Putting them side by side means the fallback is a tab away, and
// the ticket already carries the breadcrumb trail of what the operator was doing.

import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

// The suggestions are the questions this bot is ACTUALLY good at, not a demo of breadth. Each maps
// onto a tool it has: registration triage, number lookup, service state.
const STARTERS = [
  'Why isn’t extension … registered?',
  'What is the service status for …?',
  'Who owns the number …?',
];

export default function AssistantChat({ page = null }) {
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState('');
  const endRef = useRef(null);

  // `?.scrollIntoView?.()` — the method is absent in jsdom, and a component that throws in a test
  // environment is a component that can throw in a browser that lacks it too. Keeping the newest
  // turn in view is a nicety; it must not be load-bearing.
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: 'end' }); }, [turns]);

  const ask = useMutation({
    // The whole conversation goes each time — the worker holds no session, so the transcript in
    // this component IS the state. It is validated server-side rather than trusted, which is why
    // sending it is safe.
    mutationFn: (history) => api.post('/assistant/messages', {
      messages: history.map((t) => ({ role: t.role, content: t.content })),
      ...(page ? { page } : {}),
    }, undefined, { timeoutMs: 90_000 }),
    onSuccess: (res) => {
      setTurns((prev) => [...prev, {
        role: 'assistant',
        // A hit turn-limit is not an answer; the route returns `answer: null` and says why.
        content: res.answer || res.error || 'No answer came back.',
        tools: res.tools || [],
        failed: !res.answer,
      }]);
    },
    onError: (err) => {
      setTurns((prev) => [...prev, {
        role: 'assistant',
        content: err?.body?.hint
          ? `${err.body.error} ${err.body.hint}`
          : (err?.body?.error || err?.message || 'The assistant could not answer.'),
        failed: true,
      }]);
    },
  });

  const send = (text) => {
    const q = String(text ?? draft).trim();
    if (!q || ask.isPending) return;
    const history = [...turns, { role: 'user', content: q }];
    setTurns(history);
    setDraft('');
    ask.mutate(history);
  };

  return (
    <div className="mt-4 flex h-96 flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3">
        {turns.length === 0 && (
          <div className="text-sm text-gray-500">
            <p>
              Ask about this org’s phone service. It reads live records — subscribers, numbers,
              devices, call history — and <strong>changes nothing</strong>.
            </p>
            <ul className="mt-2 space-y-1">
              {STARTERS.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    onClick={() => setDraft(s.replace('…', ''))}
                    className="cursor-pointer text-left text-blue-600 hover:underline"
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className={t.role === 'user' ? 'text-right' : ''}>
            <div
              className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                t.role === 'user'
                  ? 'bg-blue-600 text-left text-white'
                  : t.failed
                    ? 'border border-amber-200 bg-amber-50 text-amber-900'
                    : 'border border-gray-200 bg-white text-gray-800'
              }`}
            >
              {t.content}
            </div>
            {/* WHAT IT READ, under the answer. An operator about to act on something about a
                customer's service should be able to see where it came from — and it is the
                difference between a claim and a reading. */}
            {t.role === 'assistant' && t.tools?.length > 0 && (
              <p className="mt-1 text-[11px] text-gray-400">
                read: {t.tools.map((x) => x.tool + (x.ok ? '' : ' (failed)')).join(', ')}
              </p>
            )}
          </div>
        ))}

        {ask.isPending && <p className="text-sm text-gray-400">Looking it up…</p>}
        <div ref={endRef} />
      </div>

      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
          placeholder="Ask about a number, an extension, an account…"
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => send()}
          disabled={!draft.trim() || ask.isPending}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Ask
        </button>
      </div>
    </div>
  );
}
